import { useAuth0 } from '@auth0/auth0-react';
import {
  Button,
  ClickAwayListener,
  Grow,
  ListItemIcon, ListItemText, MenuItem, MenuList, Paper, Popper
} from '@material-ui/core';
import { ChevronRight, ExpandMore } from '@material-ui/icons';
import AuthorizedContent from 'components/authorized-content';
import { ConfirmationDialog } from 'components/confirmation-dialog/confirmation-dialog';
import { AccountOutlinedIcon } from 'components/icons/account-outlined';
import { CompanyManagementIcon } from 'components/icons/company-management';
import { ProfileSettingsIcon } from 'components/icons/profile-settings';
import { ReducerAction, UserRole, useUserContext } from 'providers/user';
import React, { memo } from 'react';
import { Link, useNavigation } from 'react-navi';
import { isFeatureEnabled } from 'utils/feature-flags';
import { removeUserFromStorage } from 'utils/storage-utils';
import userApi from '../../api/auth/user/user-api';
import { environment } from '../../environment';
import { useApiReadiness } from '../../providers/api-readiness/api-readiness-context';
import { useAuth0Linking } from '../../providers/auth0-linking-status/auth0-linking-context';
import { AccountMenuProps } from './account-menu-props';
import style from './account-menu.module.scss';

/**
 * Confirm a user email address page.
 * @param props
 */
export const AccountMenu: React.FC<AccountMenuProps> = memo((props) => {
  const {
    name,
    email,
    myMLAIconUrl,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [companyMenuOpen, setCompanyMenuOpen] = React.useState(false);
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [hasCompanyAccess, setHasCompanyAccess] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const companyMenuAnchorRef = React.useRef<HTMLLIElement>(null);
  const navigation = useNavigation();
  const { state: userState, dispatch: userDispatch, refreshUser } = useUserContext();
  const { dispatch: auth0LinkingDispatch } = useAuth0Linking();
  const { logout } = useAuth0();
  const isApiReady = useApiReadiness();

  const handleToggle = async () => {
    // If opening the menu, check user status to verify company access
    if (!open && !isRefreshing) {
      setIsRefreshing(true);
      try {
        const hasCompany = (await userApi.getCurrentUserStatus()).hasCompany;
        setHasCompanyAccess(hasCompany);
      } catch (error) {
        console.error('Failed to refresh user context:', error);
      } finally {
        setIsRefreshing(false);
      }
    }
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event?: React.MouseEvent<EventTarget>) => {
    if (event && anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
    setCompanyMenuOpen(false);
  };

  const handleCompanyMenuToggle = () => {
    setCompanyMenuOpen((prev) => !prev);
  };

  const handleCompanyMenuClose = () => {
    setCompanyMenuOpen(false);
  };

  const logOut = () => {
    removeUserFromStorage();
    userDispatch({ type: ReducerAction.Reset });
    auth0LinkingDispatch({ type: ReducerAction.Reset });
    logout({ logoutParams: { returnTo: environment.auth0.logOutRedirectUri } });
  };

  const gotoMyProfile = () => {
    navigation.navigate('/user/details');
    handleClose();
  };

  const gotoMyCompanyDetails = () => {
    navigation.navigate('/producer/my-company');
    handleClose();
  };

  const gotoMyCompanyFlocks = () => {
    navigation.navigate('/producer/my-company/flocks');
    handleClose();
  };

  const gotoMyCompanyUsers = () => {
    navigation.navigate('/producer/my-company/users');
    handleClose();
  };

  const gotoMyCompanyPurchaseHistory = () => {
    navigation.navigate('/producer/my-company/purchase-history');
    handleClose();
  };

  const gotoMyCompanyTools = () => {
    navigation.navigate('/producer/my-company/tools');
    handleClose();
  };

  const handleRegisterCompany = () => {
    navigation.navigate('/account/register/company');
    handleClose();
  }

  function initials(name: string): React.ReactNode {
    const nameParts = name.trim().split(' ');
    if (nameParts.length === 0) {
      return '';
    }
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
    return (
      <>
        {nameParts[0].charAt(0).toUpperCase()}
        {nameParts[nameParts.length - 1].charAt(0).toUpperCase()}
      </>
    );
  }

  return (
    <>
      {isApiReady && (
        <>
          <div className={style.sgAccountMenu}>
            {/* <AuthorizedContent roles={[UserRole.CompanyUser, UserRole.Breeder, UserRole.ServiceProvider]}>
              <Button
                className="sg-account-menu__button outlined"
                onClick={() => {
                  navigation.navigate('/producer/my-company');
                }}
              >
                <span className="desktop">My Company</span>
                <CompanyManagementIcon className="mobile" />
              </Button>
            </AuthorizedContent> */}
            <AuthorizedContent roles={[UserRole.Admin]}>
              <Button
                className={`${style.button} ${style.admin}`}
                color="secondary"
                onClick={() => {
                  navigation.navigate('/admin');
                }}
              >
                <span className={style.desktop}>Admin Page</span>
                <span className={style.mobile}>Admin</span>
              </Button>
            </AuthorizedContent>
            {!name ? (
              <Button
              className={`${style.button} ${open ? style.selected : ''} ${style.profile} ${style.guest}`}
              ref={anchorRef}
              onClick={handleToggle}
            >
              <AccountOutlinedIcon />
            </Button>
            ) : (
            <Button
              className={`${style.button} ${open ? style.selected : ''} ${style.profile}`}
              ref={anchorRef}
              onClick={handleToggle}
            >
              <span className={style.mobile}>{initials(name)}</span>
              <span className={style.desktop}>{name}</span>
              <AccountOutlinedIcon className={initials(name) ? style.desktop : style.mobile} />
            </Button>
            )}
            <Popper
              className={style.paper}
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              transition
              disablePortal
              placement="bottom-end"
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
                >
                  <Paper>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList>
                        <MenuItem onClick={gotoMyProfile}>
                          <ListItemIcon>
                            <ProfileSettingsIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>
                            My Profile
                          </ListItemText>
                        </MenuItem>
                        {hasCompanyAccess ? (
                          <AuthorizedContent roles={[UserRole.Admin, UserRole.Breeder, UserRole.CompanyUser, UserRole.ServiceProvider]}>
                            <MenuItem 
                              ref={companyMenuAnchorRef}
                              onClick={handleCompanyMenuToggle}
                            >
                              <ListItemIcon>
                                <CompanyManagementIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>
                                My Company
                              </ListItemText>
                              <ListItemIcon style={{ marginLeft: 'auto', minWidth: 'auto' }}>
                                {companyMenuOpen ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                              </ListItemIcon>
                            </MenuItem>
                          </AuthorizedContent>
                        ) : (
                        <Button
                          className={style.registerCompany}
                          onClick={() => setOpenConfirmation(true)}
                          variant="text"
                        >
                          Manage Sheep
                        </Button>
                        )}
                        <Button
                          className={style.logout}
                          onClick={logOut}
                          variant="outlined"
                        >
                          Log out
                        </Button>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
            
            {hasCompanyAccess && (
              <Popper
                className={style.companyMenuPopper}
                open={companyMenuOpen}
                anchorEl={companyMenuAnchorRef.current}
                role={undefined}
                transition
                disablePortal
                placement="right-start"
                style={{ zIndex: 1301 }}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{ transformOrigin: placement === 'right' ? 'left center' : 'right center' }}
                  >
                    <Paper className={style.paper}>
                      <ClickAwayListener onClickAway={handleCompanyMenuClose}>
                        <MenuList>
                          <MenuItem onClick={gotoMyCompanyDetails}>
                            <ListItemText>
                              Company Details
                            </ListItemText>
                          </MenuItem>
                          <MenuItem onClick={gotoMyCompanyFlocks}>
                            <ListItemText>
                              Flock Management
                            </ListItemText>
                          </MenuItem>
                          <MenuItem onClick={gotoMyCompanyUsers}>
                            <ListItemText>
                              User Management
                            </ListItemText>
                          </MenuItem>
                          <MenuItem onClick={gotoMyCompanyPurchaseHistory}>
                            <ListItemText>
                              Purchase History
                            </ListItemText>
                          </MenuItem>
                          {isFeatureEnabled('gbv') && (
                            <MenuItem onClick={gotoMyCompanyTools}>
                              <ListItemText>
                                Tools
                              </ListItemText>
                            </MenuItem>
                          )}
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            )}
          </div>
        </>
      )}

      <ConfirmationDialog
        confirmLabel="Yes, I want to Manage Sheep"
        cancelLabel="Back"
        title="Do you want to Manage sheep registered with Sheep Genetics?"
        onCancel={() => setOpenConfirmation(false)}
        onConfirm={() => handleRegisterCompany()}
        open={openConfirmation}
      >
        <p>Additional functionality is available within the Sheep Genetics site for ram breeders that have flocks registered with Sheep Genetics.​</p>
        <p>To manage flocks registered with Sheep Genetics, you must provide additional information about the company you are affiliated with.</p>
        <p>Already have an account? Contact Sheep Genetics at <Link href="mailto:info@sheepgenetics.org.au">info@sheepgenetics.org.au</Link> or on <Link href='tel:02 8055 1818'>02 8055 1818</Link>.</p>
      </ConfirmationDialog>
    </>
  );
});

(AccountMenu as any).whyDidYouRender = true;
